import React from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { AppTopBar } from '../components/AppTopBar';
import { AppSidebar } from '../components/AppSidebar';
import { AppSelect } from '../components/AppSelect';
import { StammdatenOrdnerSidebar } from '../components/StammdatenOrdnerSidebar';
import { LeistungenSection } from '../features/stammdaten/sections/LeistungenSection';
import { useStammdatenFolderModals } from '../features/stammdaten/components/StammdatenFolderModals';
import { VorlagenSection } from '../features/stammdaten/sections/VorlagenSection';
import { VorlageEditor } from '../features/stammdaten/components/VorlageEditor';
import { MaterialienSection } from '../features/stammdaten/sections/MaterialienSection';
import type { MaterialFolder, SupplierCatalog } from '../features/stammdaten/types/material';
import type { LeistungFolder } from '../features/stammdaten/types/leistung';
import type { OfferingVorlageFolder } from '../features/stammdaten/types';
import { truncateFolderNameLabel } from '../features/stammdaten/utils/folderName';
import {
  stammdatenPath,
  stammdatenTabFromSlug,
  type StammdatenTab,
} from '../utils/stammdatenRoutes';
import { Plus } from 'lucide-react';
import { useCapabilities } from '../hooks/useCapabilities';

export const StammdatenPage: React.FC = () => {
  const navigate = useNavigate();
  const { can, loaded: capabilitiesLoaded } = useCapabilities();
  const { instanceId: instanceIdParam, tabSlug, vorlageId } = useParams<{
    instanceId: string;
    tabSlug?: string;
    vorlageId?: string;
  }>();
  const instanceId = instanceIdParam ?? 'default';
  const canManageSupplierCatalogs = can('web.stammdaten.edit');
  // LV-Vorlagen laufen über /offering-vorlagen und damit über das Modul „Vorlagen“.
  const canViewVorlagen = can('web.vorlagen.view');
  const stammdatenTab: StammdatenTab = vorlageId
    ? 'vorlagen'
    : stammdatenTabFromSlug(tabSlug) ?? 'materialien';
  const navigateStammdatenTab = React.useCallback(
    (tab: StammdatenTab) => {
      navigate(stammdatenPath(instanceId, tab));
    },
    [navigate, instanceId]
  );

  const [folders, setFolders] = React.useState<MaterialFolder[]>([]);
  const [leistungFolders, setLeistungFolders] = React.useState<LeistungFolder[]>([]);
  const [vorlageFolders, setVorlageFolders] = React.useState<OfferingVorlageFolder[]>([]);
  const [vorlagenTotalCount, setVorlagenTotalCount] = React.useState(0);
  const [totalLeistungenCount, setTotalLeistungenCount] = React.useState(0);
  const [totalMaterialsCount, setTotalMaterialsCount] = React.useState(0);
  const [selectedMaterialFolderId, setSelectedMaterialFolderId] = React.useState<string | null>(null);
  const [selectedLeistungFolderId, setSelectedLeistungFolderId] = React.useState<string | null>(null);
  const [selectedVorlageFolderId, setSelectedVorlageFolderId] = React.useState<string | null>(null);
  const [supplierCatalogs, setSupplierCatalogs] = React.useState<SupplierCatalog[]>([]);
  const [selectedSupplierId, setSelectedSupplierId] = React.useState<string | null>(null);
  const [folderMenuId, setFolderMenuId] = React.useState<string | null>(null);
  const [mobileCreateOpen, setMobileCreateOpen] = React.useState(false);
  const [mountedTabs, setMountedTabs] = React.useState<Set<StammdatenTab>>(
    () => new Set(vorlageId ? [] : [stammdatenTab])
  );
  const foldersLoadedRef = React.useRef<Record<StammdatenTab, boolean>>({
    materialien: false,
    leistungen: false,
    vorlagen: false,
  });

  const ordnerFolders = React.useMemo(
    () => folders.filter((f) => f.section === 'ordner' || f.section === 'datanorm'),
    [folders]
  );

  const materialOrdnerSidebar = React.useMemo(
    () =>
      ordnerFolders.map((f) => ({
        id: String(f.id),
        name: f.name,
        itemCount: Number(f.material_count) || 0,
      })),
    [ordnerFolders]
  );
  const supplierCatalogSidebar = React.useMemo(
    () =>
      supplierCatalogs.map((s) => ({
        id: s.id,
        name: s.name,
        itemCount: Number(s.material_count) || 0,
        logoUrl: s.logo_url,
      })),
    [supplierCatalogs]
  );
  const leistungOrdnerSidebar = React.useMemo(
    () =>
      leistungFolders.map((f) => ({
        id: String(f.id),
        name: f.name,
        itemCount: Number(f.leistung_count) || 0,
      })),
    [leistungFolders]
  );
  const vorlageOrdnerSidebar = React.useMemo(
    () =>
      vorlageFolders.map((f) => ({
        id: String(f.id),
        name: f.name,
        itemCount: Number(f.vorlage_count) || 0,
      })),
    [vorlageFolders]
  );

  const sidebarOrdnerFolders =
    stammdatenTab === 'materialien'
      ? materialOrdnerSidebar
      : stammdatenTab === 'leistungen'
        ? leistungOrdnerSidebar
        : vorlageOrdnerSidebar;

  const clearMaterialSidebarFilters = React.useCallback(() => {
    setSelectedMaterialFolderId(null);
    setSelectedSupplierId(null);
  }, []);

  const loadFolders = React.useCallback((): Promise<void> => {
    return fetch('/api/material-folders')
      .then((r) => (r.ok ? r.json() : []))
      .then((data) => setFolders(Array.isArray(data) ? data : []))
      .catch(() => setFolders([]));
  }, []);

  const loadSuppliers = React.useCallback((): Promise<void> => {
    return fetch('/api/suppliers')
      .then((r) => (r.ok ? r.json() : { items: [] }))
      .then((data) => {
        const items = Array.isArray(data?.items) ? (data.items as SupplierCatalog[]) : [];
        setSupplierCatalogs(items);
        setSelectedSupplierId((prev) => {
          if (!prev) return prev;
          return items.some((s) => s.id === prev) ? prev : null;
        });
      })
      .catch(() => setSupplierCatalogs([]));
  }, []);

  const loadLeistungFolders = React.useCallback((): Promise<void> => {
    return fetch('/api/leistung-folders')
      .then((r) => (r.ok ? r.json() : []))
      .then((data) => setLeistungFolders(Array.isArray(data) ? data : []))
      .catch(() => setLeistungFolders([]));
  }, []);

  const loadVorlageFolders = React.useCallback((): Promise<void> => {
    return fetch('/api/offering-vorlage-folders')
      .then((r) => (r.ok ? r.json() : []))
      .then((data) => {
        const rows = Array.isArray(data) ? (data as OfferingVorlageFolder[]) : [];
        setVorlageFolders(rows);
      })
      .catch(() => setVorlageFolders([]));
  }, []);

  const leistungOrdnerIds = React.useMemo(
    () => new Set(leistungFolders.map((f) => f.id)),
    [leistungFolders]
  );

  const { openOrdnerModal, requestFolderDelete, folderModals } = useStammdatenFolderModals({
    onMaterialFolderCreated: (folderId) => {
      setSelectedMaterialFolderId(folderId);
      setSelectedSupplierId(null);
    },
    onLeistungFolderCreated: setSelectedLeistungFolderId,
    onVorlageFolderCreated: setSelectedVorlageFolderId,
    onMaterialFolderDeleted: (folderId) => {
      setFolders((prev) => prev.filter((f) => f.id !== folderId));
      setSelectedMaterialFolderId((prev) => (prev === folderId ? null : prev));
    },
    onLeistungFolderDeleted: (folderId) => {
      setLeistungFolders((prev) => prev.filter((f) => f.id !== folderId));
      setSelectedLeistungFolderId((prev) => (prev === folderId ? null : prev));
    },
    onVorlageFolderDeleted: (folderId) => {
      setVorlageFolders((prev) => prev.filter((f) => f.id !== folderId));
      setSelectedVorlageFolderId((prev) => (prev === folderId ? null : prev));
    },
    refreshMaterialFolders: loadFolders,
    refreshLeistungFolders: loadLeistungFolders,
    refreshVorlageFolders: loadVorlageFolders,
  });

  React.useEffect(() => {
    if (vorlageId) return;
    setMountedTabs((prev) => {
      if (prev.has(stammdatenTab)) return prev;
      const next = new Set(prev);
      next.add(stammdatenTab);
      return next;
    });
  }, [stammdatenTab, vorlageId]);

  React.useEffect(() => {
    if (stammdatenTab === 'materialien' && !foldersLoadedRef.current.materialien) {
      foldersLoadedRef.current.materialien = true;
      void loadFolders();
      void loadSuppliers();
    } else if (stammdatenTab === 'leistungen' && !foldersLoadedRef.current.leistungen) {
      foldersLoadedRef.current.leistungen = true;
      void loadLeistungFolders();
    } else if (stammdatenTab === 'vorlagen' && canViewVorlagen && !foldersLoadedRef.current.vorlagen) {
      foldersLoadedRef.current.vorlagen = true;
      void loadVorlageFolders();
    }
  }, [stammdatenTab, canViewVorlagen, loadFolders, loadSuppliers, loadLeistungFolders, loadVorlageFolders]);

  React.useEffect(() => {
    const onFocus = () => {
      if (stammdatenTab === 'materialien') void loadSuppliers();
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [loadSuppliers, stammdatenTab]);

  React.useEffect(() => {
    const onDoc = () => setFolderMenuId(null);
    document.addEventListener('click', onDoc);
    return () => document.removeEventListener('click', onDoc);
  }, []);

  const showMainTabs = !vorlageId;

  if (stammdatenTab === 'vorlagen' && capabilitiesLoaded && !canViewVorlagen) {
    return <Navigate to={stammdatenPath(instanceId, 'materialien')} replace />;
  }

  return (
    <div className="h-screen flex bg-app-bg font-sans overflow-hidden">
      <AppSidebar />
      <main className="flex-1 flex flex-col min-w-0 overflow-hidden pb-16 md:pb-0">
        <AppTopBar title="Stammdaten" />

        <div className="flex flex-col flex-1 min-h-0 overflow-hidden">
          <div className="md:hidden shrink-0 border-b border-app-border bg-app-surface flex items-center gap-2 px-3 py-2">
            <AppSelect
              value={stammdatenTab}
              onChange={(v) => {
                navigateStammdatenTab(v as StammdatenTab);
                setFolderMenuId(null);
              }}
              options={[
                { value: 'materialien', label: 'Materialien' },
                { value: 'leistungen', label: 'Leistungen' },
                ...(canViewVorlagen ? [{ value: 'vorlagen', label: 'Vorlagen' }] : []),
              ]}
              className="w-36"
            />
            {stammdatenTab === 'materialien' && (
              <AppSelect
                value={selectedMaterialFolderId ?? ''}
                onChange={(v) => {
                  setSelectedMaterialFolderId(v ? String(v) : null);
                  setSelectedSupplierId(null);
                }}
                options={[
                  { value: '', label: `Alle (${totalMaterialsCount})` },
                  ...ordnerFolders.map((f) => ({ value: f.id, label: truncateFolderNameLabel(f.name) })),
                ]}
                className="flex-1"
              />
            )}
            {stammdatenTab === 'leistungen' && (
              <AppSelect
                value={selectedLeistungFolderId ?? ''}
                onChange={(v) => setSelectedLeistungFolderId(v ? String(v) : null)}
                options={[
                  { value: '', label: `Alle (${totalLeistungenCount})` },
                  ...leistungFolders.map((f) => ({ value: f.id, label: truncateFolderNameLabel(f.name) })),
                ]}
                className="flex-1"
              />
            )}
            {stammdatenTab === 'vorlagen' && (
              <AppSelect
                value={selectedVorlageFolderId ?? ''}
                onChange={(v) => setSelectedVorlageFolderId(v ? String(v) : null)}
                options={[
                  { value: '', label: `Alle (${vorlagenTotalCount})` },
                  ...vorlageFolders.map((f) => ({ value: f.id, label: truncateFolderNameLabel(f.name) })),
                ]}
                className="flex-1"
              />
            )}
            <button
              type="button"
              onClick={() => openOrdnerModal(stammdatenTab)}
              className="shrink-0 p-2 rounded-lg text-app-text-subtle hover:bg-app-hover"
              aria-label="Ordner hinzufügen"
            >
              <Plus className="w-4 h-4" />
            </button>
          </div>

          <div className="flex flex-1 min-h-0 overflow-hidden">
            <StammdatenOrdnerSidebar
              activeTab={stammdatenTab}
              onTabChange={(tab) => {
                navigateStammdatenTab(tab);
                setFolderMenuId(null);
              }}
              ordnerFolders={sidebarOrdnerFolders}
              showVorlagenTab={canViewVorlagen}
              supplierCatalogs={supplierCatalogSidebar}
              showSupplierCatalogsSection={stammdatenTab === 'materialien'}
              selectedSupplierId={selectedSupplierId}
              onSelectSupplier={(id) => {
                setSelectedSupplierId(id);
                if (id !== null) {
                  setSelectedMaterialFolderId(null);
                }
              }}
              canManageSupplierCatalogs={canManageSupplierCatalogs}
              onSupplierCatalogsChanged={() => {
                void loadSuppliers();
              }}
              selectedFolderId={
                stammdatenTab === 'materialien'
                  ? selectedMaterialFolderId
                  : stammdatenTab === 'leistungen'
                    ? selectedLeistungFolderId
                    : selectedVorlageFolderId
              }
              onSelectFolder={(id) => {
                const normalized = id == null || id === '' ? null : String(id);
                if (stammdatenTab === 'materialien') {
                  setSelectedMaterialFolderId(normalized);
                  if (normalized !== null) setSelectedSupplierId(null);
                } else if (stammdatenTab === 'leistungen') {
                  setSelectedLeistungFolderId(normalized);
                } else {
                  setSelectedVorlageFolderId(normalized);
                }
              }}
              allItemsLabel={
                stammdatenTab === 'materialien'
                  ? 'Alle Materialien'
                  : stammdatenTab === 'leistungen'
                    ? 'Alle Leistungen'
                    : 'Alle Vorlagen'
              }
              allItemsCount={
                stammdatenTab === 'materialien'
                  ? totalMaterialsCount
                  : stammdatenTab === 'leistungen'
                    ? totalLeistungenCount
                    : vorlagenTotalCount
              }
              onAddOrdner={() => openOrdnerModal(stammdatenTab)}
              folderMenuOpenId={folderMenuId}
              onToggleFolderMenu={(folderId) => setFolderMenuId((id) => (id === folderId ? null : folderId))}
              onRequestDeleteFolder={(sf) => {
                setFolderMenuId(null);
                if (stammdatenTab === 'leistungen') {
                  const f = leistungFolders.find((x) => x.id === sf.id);
                  if (f) requestFolderDelete({ kind: 'leistung', folder: f });
                } else if (stammdatenTab === 'vorlagen') {
                  const f = vorlageFolders.find((x) => x.id === sf.id);
                  if (f) requestFolderDelete({ kind: 'vorlage', folder: f });
                } else {
                  const f = ordnerFolders.find((x) => x.id === sf.id);
                  if (f) requestFolderDelete({ kind: 'material', folder: f });
                }
              }}
            />

            <div
              className={
                vorlageId
                  ? 'app-scrollbar flex flex-1 flex-col min-h-0 min-w-0 overflow-y-auto overscroll-contain px-3 pt-3 pb-2 md:px-6 md:pt-6 md:pb-3'
                  : stammdatenTab === 'vorlagen'
                    ? 'app-scrollbar flex-1 min-h-0 min-w-0 overflow-y-auto overscroll-contain p-3 md:flex md:flex-col md:overflow-hidden md:p-6'
                    : 'app-scrollbar flex-1 min-h-0 min-w-0 overflow-y-auto overscroll-contain p-3 md:flex md:flex-col md:overflow-hidden md:p-0'
              }
            >
              {vorlageId ? (
                canViewVorlagen ? <VorlageEditor vorlageId={vorlageId} /> : null
              ) : (
                <>
                  {mountedTabs.has('vorlagen') && canViewVorlagen ? (
                    <div
                      className={
                        stammdatenTab === 'vorlagen'
                          ? 'app-scrollbar md:min-h-0 md:flex-1 md:overflow-y-auto md:overscroll-contain'
                          : 'hidden'
                      }
                    >
                      <VorlagenSection
                        selectedFolderId={selectedVorlageFolderId}
                        onFoldersRefresh={loadVorlageFolders}
                        onTotalCountChange={setVorlagenTotalCount}
                      />
                    </div>
                  ) : null}
                  {mountedTabs.has('leistungen') ? (
                    <div
                      className={
                        stammdatenTab === 'leistungen'
                          ? 'md:flex md:min-h-0 md:flex-1 md:flex-col'
                          : 'hidden'
                      }
                    >
                      <LeistungenSection
                        selectedFolderId={selectedLeistungFolderId}
                        leistungFolders={leistungFolders}
                        leistungOrdnerIds={leistungOrdnerIds}
                        onFoldersRefresh={loadLeistungFolders}
                        onTotalCountChange={setTotalLeistungenCount}
                        mobileCreateOpen={mobileCreateOpen && stammdatenTab === 'leistungen'}
                        onCloseMobileCreate={() => setMobileCreateOpen(false)}
                      />
                    </div>
                  ) : null}
                  {mountedTabs.has('materialien') ? (
                    <div
                      className={
                        stammdatenTab === 'materialien'
                          ? 'md:flex md:min-h-0 md:flex-1 md:flex-col'
                          : 'hidden'
                      }
                    >
                      <MaterialienSection
                        selectedFolderId={selectedMaterialFolderId}
                        selectedSupplierId={selectedSupplierId}
                        onClearFilters={clearMaterialSidebarFilters}
                        ordnerFolders={ordnerFolders}
                        supplierCatalogs={supplierCatalogs}
                        onFoldersRefresh={loadFolders}
                        onTotalCountChange={setTotalMaterialsCount}
                        mobileCreateOpen={mobileCreateOpen && stammdatenTab === 'materialien'}
                        onCloseMobileCreate={() => setMobileCreateOpen(false)}
                      />
                    </div>
                  ) : null}
                </>
              )}
            </div>
          </div>
        </div>

        {showMainTabs && stammdatenTab !== 'vorlagen' ? (
          <button
            type="button"
            onClick={() => setMobileCreateOpen(true)}
            className="fixed bottom-20 right-5 z-30 md:hidden h-14 w-14 rounded-full bg-green-600 text-white shadow-lg flex items-center justify-center hover:bg-green-700 active:bg-green-800"
            aria-label={stammdatenTab === 'leistungen' ? 'Neue Leistung anlegen' : 'Neues Material anlegen'}
          >
            <Plus className="w-6 h-6" />
          </button>
        ) : null}

        {folderModals}
      </main>
    </div>
  );
};
